<template>
  <div class="markdown-editor-shell" :class="{ 'is-disabled': disabled }">
    <div ref="editor"></div>
  </div>
</template>

<script>
import Vditor from 'vditor'
import 'vditor/dist/index.css'
import { renderVditorPlantUml, transformVditorPlantUml } from '@/utils/plantuml'

const formulaIcon = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 4h12v2H9.7l4.8 6-4.8 6H18v2H6l6-8-6-8Z"></path>
  </svg>
`

const diagramIcon = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 3h6v5H7v2h10V8h-3V3h6v5h-2v8h2v5h-6v-5h3v-4H7v4h3v5H4v-5h2V8H4V3Zm2 2v1h2V5H6Zm10 0v1h2V5h-2ZM6 18v1h2v-1H6Zm10 0v1h2v-1h-2Z"></path>
  </svg>
`

const apiOrigin = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '')
const VDITOR_ASSET_VERSION = '3.11.2'

function getVditorCdn() {
  const base = String(process.env.BASE_URL || '/').replace(/\/+$/, '')
  return `${window.location.origin}${base}/vendor/vditor/${VDITOR_ASSET_VERSION}`
}

export default {
  name: 'MarkdownEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    minHeight: {
      type: Number,
      default: 620
    }
  },
  data() {
    return {
      editor: null,
      editorReady: false,
      lastValue: this.value
    }
  },
  watch: {
    value(nextValue) {
      const value = String(nextValue || '')
      if (!this.editorReady || value === this.lastValue) return

      this.lastValue = value
      this.editor.setValue(value, true)
    },
    disabled(nextValue) {
      if (!this.editorReady) return
      if (nextValue) {
        this.editor.disabled()
      } else {
        this.editor.enable()
      }
    }
  },
  mounted() {
    this.createEditor()
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  },
  methods: {
    createEditor() {
      this.editor = new Vditor(this.$refs.editor, {
        cdn: getVditorCdn(),
        lang: 'zh_CN',
        mode: 'sv',
        value: this.value,
        minHeight: this.minHeight,
        placeholder: '从这里开始写作。支持 Markdown、数学公式、Mermaid 和 PlantUML 图表。',
        cache: {
          enable: false
        },
        counter: {
          enable: true,
          type: 'markdown'
        },
        resize: {
          enable: true,
          position: 'bottom'
        },
        toolbarConfig: {
          pin: false
        },
        toolbar: [
          'headings',
          'bold',
          'italic',
          'strike',
          'link',
          '|',
          'list',
          'ordered-list',
          'check',
          'quote',
          '|',
          'code',
          'inline-code',
          'table',
          {
            name: 'diagram',
            icon: diagramIcon,
            tip: '插入图表',
            tipPosition: 'n',
            toolbar: [
              {
                name: 'mermaid-diagram',
                icon: diagramIcon,
                tip: 'Mermaid 图表',
                tipPosition: 'n',
                prefix: '\n```mermaid\n',
                suffix: '\n```\n'
              },
              {
                name: 'plantuml-diagram',
                icon: diagramIcon,
                tip: 'PlantUML 图表',
                tipPosition: 'n',
                prefix: '\n```plantuml\n@startuml\n',
                suffix: '\n@enduml\n```\n'
              }
            ]
          },
          {
            name: 'formula',
            icon: formulaIcon,
            tip: '插入公式',
            tipPosition: 'n',
            toolbar: [
              {
                name: 'inline-formula',
                icon: formulaIcon,
                tip: '行内公式 $…$',
                tipPosition: 'n',
                prefix: '$',
                suffix: '$'
              },
              {
                name: 'block-formula',
                icon: formulaIcon,
                tip: '块级公式 $$…$$',
                tipPosition: 'n',
                prefix: '\n$$\n',
                suffix: '\n$$\n'
              }
            ]
          },
          'upload',
          '|',
          'undo',
          'redo',
          '|',
          'both',
          'preview',
          'outline',
          'fullscreen'
        ],
        customRenders: [
          {
            language: 'plantuml-server',
            render: renderVditorPlantUml
          }
        ],
        preview: {
          delay: 300,
          maxWidth: 960,
          mode: 'both',
          hljs: {
            enable: true,
            lineNumber: false,
            style: 'github'
          },
          markdown: {
            autoSpace: false,
            codeBlockPreview: true,
            fixTermTypo: false,
            footnotes: true,
            gfmAutoLink: true,
            listStyle: false,
            mark: true,
            mathBlockPreview: true,
            paragraphBeginningSpace: false,
            sanitize: true,
            toc: false
          },
          math: {
            engine: 'KaTeX',
            inlineDigit: false,
            macros: {}
          },
          theme: {
            current: 'light'
          },
          transform: transformVditorPlantUml
        },
        upload: {
          url: `${apiOrigin}/api/v1/upload`,
          accept: 'image/jpeg,image/png,image/gif,image/webp',
          max: 10 * 1024 * 1024,
          multiple: false,
          fieldName: 'file',
          setHeaders: () => {
            const token = localStorage.getItem('token')
            return token ? { Authorization: `Bearer ${token}` } : {}
          },
          format: (files, responseText) => {
            try {
              const response = JSON.parse(responseText)
              const file = files[0]
              const success = Number(response.status) === 200 && response.url
              return JSON.stringify({
                msg: success ? '' : (response.message || '图片上传失败'),
                code: success ? 0 : 1,
                data: {
                  errFiles: success ? [] : [file.name],
                  succMap: success ? { [file.name]: response.url } : {}
                }
              })
            } catch (error) {
              return JSON.stringify({
                msg: '无法解析上传结果',
                code: 1,
                data: { errFiles: files.map(file => file.name), succMap: {} }
              })
            }
          }
        },
        after: () => {
          this.editorReady = true
          this.lastValue = this.value
          this.applyAccessibilityLabels()
          if (this.disabled) this.editor.disabled()
          this.$emit('ready')
        },
        input: value => {
          this.lastValue = value
          this.$emit('input', value)
        },
        keydown: event => {
          if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
            event.preventDefault()
            this.$emit('save')
          }
        },
        ctrlEnter: () => {
          this.$emit('save')
        }
      })
    },
    applyAccessibilityLabels() {
      const editor = this.$refs.editor
      if (!editor) return

      const editable = editor.querySelector('.vditor-sv, .vditor-ir, .vditor-wysiwyg')
      if (editable) {
        editable.setAttribute('role', 'textbox')
        editable.setAttribute('aria-multiline', 'true')
        editable.setAttribute('aria-label', '文章 Markdown 内容')
      }
    },
    focus() {
      if (this.editorReady) this.editor.focus()
    }
  }
}
</script>

<style scoped>
.markdown-editor-shell {
  min-width: 0;
}

.markdown-editor-shell :deep(.vditor) {
  border-color: rgba(107, 95, 76, 0.18);
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(45, 55, 72, 0.03);
}

.markdown-editor-shell :deep(.vditor-toolbar) {
  padding: 0.45rem 0.65rem;
  border-bottom-color: rgba(107, 95, 76, 0.14);
  background: #f8f5ef;
}

.markdown-editor-shell :deep(.vditor-toolbar__item) {
  margin: 0 1px;
}

.markdown-editor-shell :deep(.vditor-toolbar button) {
  min-width: 34px;
  min-height: 34px;
  border-radius: 6px;
}

.markdown-editor-shell :deep(.vditor-toolbar button:hover),
.markdown-editor-shell :deep(.vditor-toolbar button:focus-visible) {
  background: rgba(67, 97, 238, 0.08);
}

.markdown-editor-shell :deep(.vditor-sv__editor),
.markdown-editor-shell :deep(.vditor-sv__preview),
.markdown-editor-shell :deep(.vditor-ir),
.markdown-editor-shell :deep(.vditor-wysiwyg) {
  background: #fff;
}

.markdown-editor-shell :deep(.vditor-sv__editor) {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 15px;
  line-height: 1.75;
}

.markdown-editor-shell :deep(.vditor-preview__action) {
  display: none;
}

.markdown-editor-shell :deep(.plantuml-preview) {
  margin: 1.25rem 0;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  overflow: hidden;
  background: #fff;
}

.markdown-editor-shell :deep(.plantuml-preview figcaption) {
  padding: 0.65rem 0.85rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f6f8fa;
  color: #64748b;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.78rem;
}

.markdown-editor-shell :deep(.plantuml-renderer) {
  min-height: 132px;
  padding: 1.25rem;
  text-align: center;
}

.markdown-editor-shell :deep(.plantuml-image) {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto 1rem;
}

.markdown-editor-shell :deep(.plantuml-status) {
  margin: 2rem 0;
  color: #64748b;
}

.markdown-editor-shell :deep(.plantuml-renderer.is-render-error .plantuml-status) {
  color: #9b2c2c;
}

.markdown-editor-shell :deep(.plantuml-source) {
  margin-top: 0.75rem;
  text-align: left;
}

.markdown-editor-shell :deep(.plantuml-source summary) {
  width: fit-content;
  min-height: 32px;
  color: #64748b;
  cursor: pointer;
}

.markdown-editor-shell :deep(.plantuml-source pre) {
  margin: 0.75rem 0 0;
}

.markdown-editor-shell :deep(.vditor-counter) {
  color: var(--text-secondary);
  background: rgba(248, 245, 239, 0.92);
}

.markdown-editor-shell.is-disabled {
  opacity: 0.78;
}
</style>
